import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import RiraLogo from "../Components/RiraLogo";

const API = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const PanelIcon = ({ path, size, x, y, rotate, opacity, fill }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24"
    fill={fill ? "currentColor" : "none"}
    stroke="currentColor" strokeWidth="1.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{
      position: "absolute", left: x, top: y,
      transform: `rotate(${rotate}deg)`,
      opacity, color: "#ffffff", pointerEvents: "none",
    }}
  >
    {path}
  </svg>
);

const PANEL_ICONS = [
  { path: <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>, size: 88, x: "6%",  y: "8%",  rotate: -15, opacity: 0.14, fill: true  },
  { path: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>,                                    size: 68, x: "70%", y: "6%",  rotate:  12, opacity: 0.11 },
  { path: <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></>,size: 76, x: "12%", y: "55%", rotate: -8,  opacity: 0.12 },
  { path: <><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></>,                     size: 72, x: "62%", y: "44%", rotate:  18, opacity: 0.10 },
  { path: <><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></>,                             size: 60, x: "4%",  y: "78%", rotate: -22, opacity: 0.12 },
  { path: <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,          size: 64, x: "76%", y: "70%", rotate:   8, opacity: 0.11 },
  { path: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>, size: 78, x: "44%", y: "18%", rotate: -5, opacity: 0.10 },
  { path: <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>, size: 48, x: "54%", y: "83%", rotate:  30, opacity: 0.13, fill: true },
  { path: <><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></>, size: 62, x: "32%", y: "70%", rotate: -12, opacity: 0.09 },
];

function AuthPage() {
  const [params]  = useSearchParams();
  const navigate  = useNavigate();
  const [mode,     setMode]     = useState(params.get("tab") === "register" ? "register" : "login");
  const [name,     setName]     = useState("");
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  const [pendingAuth,    setPendingAuth]    = useState(null);
  const [pendingEmail,   setPendingEmail]   = useState("");
  const [devCode,        setDevCode]        = useState("");
  const [verifyCode,     setVerifyCode]     = useState("");
  const [verifyLoading,  setVerifyLoading]  = useState(false);
  const [verifyError,    setVerifyError]    = useState("");

  const { login, register, completeAuth } = useAuth();

  const containerRef = useRef(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      if (containerRef.current) {
        containerRef.current.style.height = vv.height + "px";
        containerRef.current.style.top = vv.offsetTop + "px";
      }
    };
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => { vv.removeEventListener("resize", sync); vv.removeEventListener("scroll", sync); };
  }, []);

  const switchMode = (m) => { setMode(m); setError(""); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        if (!name.trim()) { setError("Full name is required."); setLoading(false); return; }
        const data = await register(name.trim(), email, password);
        setPendingAuth({ token: data.token, user: data.user });
        setPendingEmail(email);
        if (data.verification_code) {
          setDevCode(data.verification_code);
          setVerifyCode(data.verification_code);
        }
        setMode("verify");
      }
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setVerifyError("");
    setVerifyLoading(true);
    try {
      const res  = await fetch(`${API}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail, token: verifyCode.trim().toUpperCase() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Invalid code.");
      completeAuth(pendingAuth.token, { ...pendingAuth.user, email_verified: 1 });
    } catch (err) {
      setVerifyError(err.message);
    }
    setVerifyLoading(false);
  };

  const handleSkipVerification = () => completeAuth(pendingAuth.token, pendingAuth.user);

  if (mode === "verify") {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-brand" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <RiraLogo size={40} showTagline layout="vertical" />
          </div>
          <div className="auth-verify-icon">✉</div>
          <div className="auth-section-title">Check your email</div>
          <p className="auth-note" style={{ marginBottom: 20 }}>
            We sent a 6-character verification code to <strong>{pendingEmail}</strong>.
          </p>
          {devCode && (
            <div className="auth-dev-code-box">
              <span className="auth-dev-code-label">Dev mode — your code:</span>
              <span className="auth-dev-code">{devCode}</span>
            </div>
          )}
          <form className="auth-form" onSubmit={handleVerify}>
            <div className="auth-field">
              <label>Verification code</label>
              <input
                type="text" placeholder="e.g. A3F9B2"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.toUpperCase())}
                maxLength={6} autoFocus autoComplete="off"
              />
            </div>
            {verifyError && <div className="auth-error">{verifyError}</div>}
            <button type="submit" className="auth-submit" disabled={verifyLoading || verifyCode.length < 6}>
              {verifyLoading ? <span className="auth-spinner" /> : "Verify & Continue"}
            </button>
          </form>
          <button className="auth-link-btn" style={{ marginTop: 14, display: "block", textAlign: "center" }} onClick={handleSkipVerification}>
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page auth-page--split" ref={containerRef}>

      {/* ── Left panel ──────────────────────────────────── */}
      <div className="auth-panel">
        <div className="auth-panel-icons">
          {PANEL_ICONS.map((ic, i) => <PanelIcon key={i} {...ic} />)}
        </div>
        <div className="auth-panel-content">
          <div className="auth-panel-brand" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <RiraLogo size={34} nameColor="#FFFFFF" />
          </div>
          <h2 className="auth-panel-headline">
            Know exactly what your customers are saying.
          </h2>
          <p className="auth-panel-sub">
            AI-powered review intelligence for local businesses. Turn customer feedback into clear, actionable insights.
          </p>
          <div className="auth-panel-stats">
            <div className="auth-panel-stat">
              <span className="auth-panel-stat-num">10k+</span>
              <span className="auth-panel-stat-lbl">Reviews analysed</span>
            </div>
            <div className="auth-panel-stat">
              <span className="auth-panel-stat-num">&lt;5s</span>
              <span className="auth-panel-stat-lbl">AI reply time</span>
            </div>
            <div className="auth-panel-stat">
              <span className="auth-panel-stat-num">10</span>
              <span className="auth-panel-stat-lbl">Intelligence signals</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right side — form ───────────────────────────── */}
      <div className="auth-form-side">
        <div className="auth-card auth-card--split">

          {/* Logo shown only on mobile (panel content hidden there) */}
          <div className="auth-mobile-brand" style={{ cursor: "pointer" }} onClick={() => navigate("/")}>
            <RiraLogo size={30} nameColor="#FFFFFF" />
          </div>

          <h2 className="auth-split-title">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="auth-split-sub">
            {mode === "login" ? "Sign in to your RIRA account." : "Start your free account — no card required."}
          </p>

          <div className="auth-tabs">
            <button className={`auth-tab ${mode === "login" ? "active" : ""}`} onClick={() => switchMode("login")}>
              Sign in
            </button>
            <button className={`auth-tab ${mode === "register" ? "active" : ""}`} onClick={() => switchMode("register")}>
              Create Account
            </button>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            {mode === "register" && (
              <div className="auth-field">
                <label>Full Name</label>
                <input type="text" placeholder="John Smith" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </div>
            )}
            <div className="auth-field">
              <label>Email</label>
              <input type="email" placeholder="you@email.com" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            </div>
            <div className="auth-field">
              <label>Password</label>
              <input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} autoComplete={mode === "login" ? "current-password" : "new-password"} />
            </div>
            {mode === "login" && (
              <div className="auth-forgot-row">
                <button type="button" className="auth-link-btn" onClick={() => navigate("/forgot-password")}>
                  Forgot password?
                </button>
              </div>
            )}
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="auth-submit" disabled={loading}>
              {loading ? <span className="auth-spinner" /> : mode === "login" ? "Sign in" : "Create Account"}
            </button>
          </form>

          {mode === "register" && (
            <p className="auth-note">After sign up, choose a plan to start using RIRA.</p>
          )}
        </div>
      </div>

    </div>
  );
}

export default AuthPage;
